import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, reasonBadge, relTime } from '../lib/api'

const REASONS = ['', 'hard_bounce', 'complaint', 'unsubscribe', 'manual', 'soft_bounce_repeated']
const PAGE = 50

/**
 * The suppression list.
 *
 * This exists to protect the customer from themselves. Continuing to mail an address that
 * hard-bounced or complained is the single fastest way to lose sending reputation, and
 * because IPs are shared, one customer doing it damages everyone on the pool. So the list
 * is enforced server-side on every send and shown here read-mostly.
 */
export function Suppressions() {
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  const [add, setAdd] = useState('')
  const [error, setError] = useState<ApiError | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['suppressions', reason, query, page],
    queryFn: () => api.suppressions.list({
      reason: reason || undefined,
      search: query || undefined,
      limit: PAGE,
      offset: page * PAGE,
    }),
  })

  const create = useMutation({
    mutationFn: () => api.suppressions.add({ address: add.trim() }),
    onSuccess: async () => {
      setAdd('')
      setError(null)
      await qc.invalidateQueries({ queryKey: ['suppressions'] })
    },
    onError: (e) => setError(e as ApiError),
  })

  const remove = useMutation({
    mutationFn: (address: string) => api.suppressions.remove(address),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppressions'] }),
    onError: (e) => setError(e as ApiError),
  })

  const rows = data?.data ?? []
  const total = data?.total ?? 0
  const hasMore = (page + 1) * PAGE < total

  return (
    <div>
      <h1>Suppressions</h1>
      <p className="lede">
        Addresses we will not send to. Hard bounces and complaints are added automatically —
        mailing them again is the fastest way to damage the reputation of the IPs you share
        with other customers.
      </p>

      <div className="card" style={{ marginBottom: 20 }}>
        <form onSubmit={e => { e.preventDefault(); if (add.trim()) create.mutate() }}
              className="row" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: '1 1 280px', marginBottom: 0 }}>
            <label htmlFor="sa">Suppress an address</label>
            <input id="sa" type="email" placeholder="someone@example.com"
                   value={add} onChange={e => setAdd(e.target.value)} />
          </div>
          <button type="submit" disabled={!add.trim() || create.isPending}>
            {create.isPending ? 'Adding…' : 'Suppress'}
          </button>
        </form>
        {error && <div className="alert error" style={{ marginTop: 12, marginBottom: 0 }}>{error.message}</div>}
      </div>

      <div className="toolbar">
        <form onSubmit={e => { e.preventDefault(); setQuery(search.trim()); setPage(0) }}
              className="row" style={{ flex: '1 1 240px' }}>
          <input placeholder="Search addresses…" value={search}
                 onChange={e => setSearch(e.target.value)} aria-label="Search suppressions" />
          <button type="submit" className="secondary">Search</button>
        </form>

        <div className="segmented" role="tablist" aria-label="Filter by reason">
          {REASONS.map(r => (
            <button key={r || 'all'} role="tab" aria-selected={reason === r}
                    className={reason === r ? 'active' : ''}
                    onClick={() => { setReason(r); setPage(0) }}>
              {r === '' ? 'All' : (reasonBadge(r).text)}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <div className="skeleton" style={{ height: 220 }} />}

      {!isLoading && rows.length === 0 && (
        <div className="empty">
          <div className="big">⊘</div>
          <p><strong>{query || reason ? 'Nothing matches that filter' : 'No suppressions'}</strong></p>
          <p>
            {query || reason
              ? 'Try clearing the filter.'
              : 'A clean list is a good sign. Addresses appear here automatically when mail hard-bounces or a recipient complains.'}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="card flush">
            <table className="data">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Reason</th>
                  <th>Receiver response</th>
                  <th>Added</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map(s => {
                  const r = reasonBadge(s.reason)
                  return (
                    <tr key={s.address}>
                      <td className="mono small">{s.address}</td>
                      <td><span className={`badge ${r.cls}`}>{r.text}</span></td>
                      <td className="small muted mono truncate">
                        {s.smtp_response ?? <span className="muted">—</span>}
                      </td>
                      <td className="small muted" title={new Date(s.created_at).toLocaleString()}>
                        {relTime(s.created_at)}
                      </td>
                      <td className="right">
                        {s.removable
                          ? (
                            <button className="ghost"
                                    onClick={() => {
                                      if (confirm(`Remove ${s.address} from the suppression list?`)) {
                                        remove.mutate(s.address)
                                      }
                                    }}>
                              Remove
                            </button>
                          )
                          : (
                            // Complaints are a recipient telling a mailbox provider they did
                            // not want this mail. Sending again after that is the fastest
                            // route to a domain-wide block, so the option is not offered.
                            <span className="small muted" title="A recipient reported this mail as spam. Removing it risks a block affecting every domain on this IP.">
                              Permanent
                            </span>
                          )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="pager">
            <button className="secondary" disabled={page === 0}
                    onClick={() => setPage(p => Math.max(0, p - 1))}>← Newer</button>
            <span className="small muted">
              {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of {total.toLocaleString()}
            </span>
            <button className="secondary" disabled={!hasMore}
                    onClick={() => setPage(p => p + 1)}>Older →</button>
          </div>
        </>
      )}

      <h2>What gets suppressed automatically</h2>
      <div className="card flush">
        <table className="kv">
          <tbody>
            <tr>
              <td>Hard bounce</td>
              <td className="small">
                A permanent 5xx: the mailbox does not exist. Removable if you know the
                address has since been created.
              </td>
            </tr>
            <tr>
              <td>Complaint</td>
              <td className="small">
                A recipient marked your mail as spam and the provider told us. Never
                removable — this is a legal and reputational line, not a preference.
              </td>
            </tr>
            <tr>
              <td>Repeated soft bounce</td>
              <td className="small">
                A 4xx that kept failing across the full retry window. Often a full mailbox
                or a temporarily broken server, so this one is removable.
              </td>
            </tr>
            <tr>
              <td>Unsubscribed</td>
              <td className="small">
                The recipient used a one-click unsubscribe header. Removable only if they
                opt back in through your own flow.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
